import type { TripChatMobileTabId } from './TripChatMobileTabs'

/** Query param that persists the mobile Chat/Itinerary/Map tab across refresh and shared links. */
export const TRIP_CHAT_MOBILE_VIEW_QUERY_PARAM = 'view'

export const DEFAULT_TRIP_CHAT_MOBILE_TAB: TripChatMobileTabId = 'chat'

const TRIP_CHAT_MOBILE_URL_TABS: ReadonlyMap<string, TripChatMobileTabId> = new Map([
  ['chat', 'chat'],
  ['itinerary', 'list'],
  ['map', 'map'],
])

function getSearchParams(search: string): URLSearchParams {
  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
}

export function parseTripChatMobileViewParam(raw: string | null): TripChatMobileTabId {
  return raw === null ? DEFAULT_TRIP_CHAT_MOBILE_TAB :
    TRIP_CHAT_MOBILE_URL_TABS.get(raw) ?? DEFAULT_TRIP_CHAT_MOBILE_TAB
}

/**
 * Returns the tab encoded in `search`, and whether the URL should be normalized
 * (strip invalid/`view=chat` so default chat URLs stay clean).
 */
export function getTripChatMobileViewUrlState(search: string): {
  tab: TripChatMobileTabId
  shouldNormalizeUrl: boolean
} {
  const raw = getSearchParams(search).get(TRIP_CHAT_MOBILE_VIEW_QUERY_PARAM)

  if (raw === null) {
    return { tab: DEFAULT_TRIP_CHAT_MOBILE_TAB, shouldNormalizeUrl: false }
  }

  const tab = parseTripChatMobileViewParam(raw)
  return {
    tab,
    // Invalid values parse to chat; redundant view=chat should also be stripped.
    shouldNormalizeUrl: !TRIP_CHAT_MOBILE_URL_TABS.has(raw) || tab === DEFAULT_TRIP_CHAT_MOBILE_TAB,
  }
}

/** Builds the next `location.search`. Omits `view` for the default chat tab. */
export function buildSearchWithTripChatMobileView(
  search: string,
  tab: TripChatMobileTabId,
): string {
  const params = getSearchParams(search)

  if (tab === DEFAULT_TRIP_CHAT_MOBILE_TAB) {
    params.delete(TRIP_CHAT_MOBILE_VIEW_QUERY_PARAM)
  } else {
    params.set(TRIP_CHAT_MOBILE_VIEW_QUERY_PARAM, tab === 'list' ? 'itinerary' : tab)
  }

  const next = params.toString()
  return next ? `?${next}` : ''
}

export function areSearchStringsEqual(a: string, b: string): boolean {
  const normalize = (value: string) => (value.startsWith('?') ? value : value ? `?${value}` : '')
  return normalize(a) === normalize(b)
}
